import { useState, FormEvent } from 'react'
import { onCreateInvitation } from '../../lib/api/functions/functions'
import type { CollaboratorPermissions } from '../../lib/db/db.types'

const DEFAULT_PERMISSIONS: CollaboratorPermissions = {
  canManageEvents: false,
  canManageReminders: false,
}

export function CreateInvitation({ workspaceId }: { workspaceId: string }) {
  const [email, setEmail] = useState('')
  const [permissions, setPermissions] =
    useState<CollaboratorPermissions>(DEFAULT_PERMISSIONS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      await onCreateInvitation({
        workspaceId,
        email,
        permissions,
      })

      // Clear form after successful submission
      setEmail('')
      setPermissions(DEFAULT_PERMISSIONS)
    } catch (err) {
      setError('Failed to send invitation. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Permissions
        </label>
        <div className="space-y-1">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={permissions.canManageEvents}
              onChange={(e) =>
                setPermissions((prev) => ({
                  ...prev,
                  canManageEvents: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Can manage events
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={permissions.canManageReminders}
              onChange={(e) =>
                setPermissions((prev) => ({
                  ...prev,
                  canManageReminders: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Can manage reminders
            </span>
          </label>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <button
        type="submit"
        disabled={isLoading}
        className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
      >
        {isLoading ? 'Sending...' : 'Send Invitation'}
      </button>
    </form>
  )
}
